/**
 * Der Warenkorb als React-Zustand.
 *
 * Bewusst getrennt von `cart.ts`: Dort steht die reine Logik, die sich ohne
 * Browser und ohne React pruefen laesst. Hier kommt nur dazu, was ein
 * Bildschirm braucht – lesen beim Start, schreiben bei jeder Aenderung und
 * ein Ereignis, damit der Zaehler in der Kopfzeile mitbekommt, dass auf einer
 * anderen Seite etwas in den Korb gelegt wurde. Ohne dieses Ereignis bliebe
 * die Zahl stehen, bis jemand neu laedt.
 *
 * Das `storage`-Ereignis deckt den zweiten Fall ab: dieselbe Anwendung in
 * einem zweiten Tab.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  addLine,
  cartCount,
  readCart,
  removeLine,
  setLineNote,
  setLineOrderField,
  setQuantity,
  writeCart,
  CART_CHANGE_EVENT,
  CART_STORAGE_KEY,
  type CartLine,
} from './cart.js';

export function useCart() {
  const [lines, setLines] = useState<CartLine[]>(() => readCart());

  useEffect(() => {
    const aktualisieren = () => setLines(readCart());
    window.addEventListener(CART_CHANGE_EVENT, aktualisieren);
    const ausAnderemTab = (event: StorageEvent) => {
      if (event.key === null || event.key === CART_STORAGE_KEY) aktualisieren();
    };
    window.addEventListener('storage', ausAnderemTab);
    return () => {
      window.removeEventListener(CART_CHANGE_EVENT, aktualisieren);
      window.removeEventListener('storage', ausAnderemTab);
    };
  }, []);

  /** Schreibt und meldet die Aenderung; der Zustand folgt ueber das Ereignis. */
  const speichern = useCallback((naechste: CartLine[]) => {
    writeCart(naechste);
    setLines(naechste);
  }, []);

  const add = useCallback((line: CartLine) => speichern(addLine(readCart(), line)), [speichern]);
  const remove = useCallback((id: string) => speichern(removeLine(readCart(), id)), [speichern]);
  const changeQuantity = useCallback((id: string, menge: number) => speichern(setQuantity(readCart(), id, menge)), [speichern]);
  const changeNote = useCallback((id: string, note: string) => speichern(setLineNote(readCart(), id, note)), [speichern]);
  const changeOrderField = useCallback(
    (id: string, value: string) => speichern(setLineOrderField(readCart(), id, value)),
    [speichern],
  );
  const clear = useCallback(() => speichern([]), [speichern]);

  return { lines, count: cartCount(lines), add, remove, changeQuantity, changeNote, changeOrderField, clear };
}
